import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { AddLayerPanel } from './AddLayerPanel';
import { EditLayerVersionPanel } from './EditLayerVersionPanel';
import { ReviewQueuePanel } from './ReviewQueuePanel';
import {
  LayerSidebarPanelProvider,
  useLayerSidebarPanel,
} from './LayerSidebarPanel';
import { useUscgMapContext } from './UscgMapContext';
import { ViewLayersPanel } from './ViewLayersPanel';
import { DraftNewVersionPanel } from './DraftNewVersionPanel';
import { useUscgFocRequirementsFlag } from 'utils/featureFlags';
import { registerHubCopTourApi } from '../../hubCopTour/hubCopTourBridge';
import { HUB_COP_TOUR_PARAM } from '../../hubCopTour/hubCopTourSteps';
import { registerFocCopLayersTourApi } from '../../focCopLayersTour/focCopLayersTourBridge';
import { FOC_COP_LAYERS_TOUR_PARAM } from '../../focCopLayersTour/focCopLayersTourSteps';
import { registerFocFsltpTourApi } from '../../focFsltpTour/focFsltpTourBridge';
import { FOC_FSLTP_TOUR_PARAM } from '../../focFsltpTour/focFsltpTourSteps';
import { registerSitlCopTourApi } from '../../sitlCopTour/sitlCopTourBridge';

function LayersViewContent() {
  const { activePanel, setActivePanel } = useLayerSidebarPanel();
  const { setSidebarSelectedInternalLayer } = useUscgMapContext();
  const focRequirements = useUscgFocRequirementsFlag();
  const [searchParams, setSearchParams] = useSearchParams();
  const tourStartedRef = useRef(false);
  const focLayersTourStartedRef = useRef(false);
  const focFsltpTourStartedRef = useRef(false);

  useEffect(() => {
    const unregisterHub = registerHubCopTourApi({
      closeSidePanels: () => setActivePanel(null),
      openReviewQueue: () => setActivePanel('review_queue'),
    });
    const unregisterSitl = registerSitlCopTourApi({
      openReviewQueue: () => setActivePanel('review_queue'),
    });
    const unregisterFocLayers = registerFocCopLayersTourApi({
      closeSidePanels: () => setActivePanel(null),
    });
    const unregisterFsltp = registerFocFsltpTourApi({
      closeSidePanels: () => setActivePanel(null),
    });
    return () => {
      unregisterHub();
      unregisterSitl();
      unregisterFocLayers();
      unregisterFsltp();
    };
  }, [setActivePanel]);

  const wantsHubCopTour = searchParams.get(HUB_COP_TOUR_PARAM) === '1';
  const wantsFocCopLayersTour =
    searchParams.get(FOC_COP_LAYERS_TOUR_PARAM) === '1';
  const wantsFocFsltpTour = searchParams.get(FOC_FSLTP_TOUR_PARAM) === '1';

  useEffect(() => {
    if (!wantsHubCopTour) {
      tourStartedRef.current = false;
      return;
    }
    if (!focRequirements || tourStartedRef.current) return;

    const timer = window.setTimeout(() => {
      tourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(HUB_COP_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('../../hubCopTour/hubCopTour')
        .then(({ startHubCopTour }) => {
          startHubCopTour();
        })
        .catch(() => {});
    }, 400);
    return () => window.clearTimeout(timer);
  }, [wantsHubCopTour, focRequirements, setSearchParams]);

  useEffect(() => {
    if (!wantsFocCopLayersTour) {
      focLayersTourStartedRef.current = false;
      return;
    }
    if (!focRequirements || focLayersTourStartedRef.current) return;

    const timer = window.setTimeout(() => {
      focLayersTourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(FOC_COP_LAYERS_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('../../focCopLayersTour/focCopLayersTour')
        .then(({ startFocCopLayersTour }) => {
          startFocCopLayersTour();
        })
        .catch(() => {});
    }, 400);
    return () => window.clearTimeout(timer);
  }, [wantsFocCopLayersTour, focRequirements, setSearchParams]);

  useEffect(() => {
    if (!wantsFocFsltpTour) {
      focFsltpTourStartedRef.current = false;
      return;
    }
    if (!focRequirements || focFsltpTourStartedRef.current) return;

    const timer = window.setTimeout(() => {
      focFsltpTourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(FOC_FSLTP_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('../../focFsltpTour/focFsltpTour')
        .then(({ startFocFsltpTour }) => {
          startFocFsltpTour();
        })
        .catch(() => {});
    }, 400);
    return () => window.clearTimeout(timer);
  }, [wantsFocFsltpTour, focRequirements, setSearchParams]);

  if (activePanel === 'add_external_layer') {
    return (
      <AddLayerPanel onBack={() => setActivePanel(null)} layerType="external" />
    );
  }
  if (activePanel === 'add_internal_layer') {
    return (
      <AddLayerPanel onBack={() => setActivePanel(null)} layerType="internal" />
    );
  }
  if (activePanel === 'add_dhs_layer') {
    return (
      <AddLayerPanel onBack={() => setActivePanel(null)} layerType="dhs" />
    );
  }
  if (activePanel === 'add_default_layer') {
    return (
      <AddLayerPanel onBack={() => setActivePanel(null)} layerType="default" />
    );
  }

  if (activePanel === 'review_queue') {
    return <ReviewQueuePanel onBack={() => setActivePanel(null)} />;
  }

  if (activePanel === 'edit_layer_version') {
    return (
      <EditLayerVersionPanel
        onBack={() => {
          setActivePanel(null);
          setSidebarSelectedInternalLayer(null);
        }}
      />
    );
  }

  if (activePanel === 'draft_new_version') {
    return (
      <DraftNewVersionPanel
        onBack={() => {
          setActivePanel(null);
          setSidebarSelectedInternalLayer(null);
        }}
      />
    );
  }

  return (
    <ViewLayersPanel
      onAddLayer={async (layerType) => {
        if (layerType === 'external') {
          setActivePanel('add_external_layer');
        } else if (layerType === 'dhs') {
          setActivePanel('add_dhs_layer');
        } else if (layerType === 'default') {
          setActivePanel('add_default_layer');
        } else {
          setActivePanel('add_internal_layer');
        }
      }}
      onOpenReviewQueue={() => setActivePanel('review_queue')}
    />
  );
}

export const LayersView = () => {
  return (
    <div className="h-100">
      <LayerSidebarPanelProvider>
        <LayersViewContent />
      </LayerSidebarPanelProvider>
    </div>
  );
};
